import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'
import type { MemberSession } from '@/constants/memberSession'

export interface UserIdCheckResult {
  available: boolean
}

export interface SignUpPayload {
  userId: string
  password: string
  passwordConfirm: string
  name?: string
  email?: string
  phone?: string
  oauthTicket?: string
  orgName?: string
  orgCode?: string
  bizGubun?: string
  bizNum?: string
  bizCert?: string
  zipcode?: string
  address?: string
  addressDetail?: string
  refundBank?: string
  refundAccount?: string
  refundHolder?: string
  agreements: {
    age: boolean
    terms: boolean
    privacy: boolean
    marketing: boolean
  }
}

export interface BizGubunOption {
  value: string
  label: string
  groupName: string
}

export interface BizGubunListResult {
  items: BizGubunOption[]
}

export interface SignUpResult {
  userId: string
  mCode: string
  member?: MemberSession | null
}

export interface KakaoOAuthStartPayload {
  redirectUri: string
  redirect?: string
  remember?: boolean
}

export interface KakaoOAuthStartResult {
  url: string
}

export interface KakaoOAuthCompletePayload {
  code: string
  state: string
}

export interface KakaoOAuthProfile {
  provider: 'kakao'
  email: string
  name: string
  emailVerified: boolean
}

export interface KakaoOAuthCompleteResult {
  action: 'login' | 'signup'
  redirect?: string
  member?: MemberSession
  ticket?: string
  profile?: KakaoOAuthProfile
}

export interface LongtermCodeCheckResult {
  available: boolean
}

export interface LoginPayload {
  userId: string
  password: string
  /** 자동 로그인. 서버가 쿠키 수명을 12시간 대신 30일로 잡는다. */
  rememberMe?: boolean
}

export interface ProfileUpdatePayload {
  orgName: string
  orgCode: string
  bizGubun: string
  name: string
  email: string
  phone: string
  bizNum: string
  bizCert: string
  zipcode: string
  address: string
  addressDetail: string
  refundBank: string
  refundAccount: string
  refundHolder: string
}

export interface ProfileUpdateResult {
  member: MemberSession
}

export interface LoginResult {
  member: MemberSession
}

export interface SessionResult {
  member: MemberSession
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/member`

const fetchOptions: RequestInit = {
  credentials: 'include',
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드(3002)와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

export async function checkMemberUserId(userId: string) {
  const query = new URLSearchParams({ userId })
  const response = await apiFetch(`${BASE}/user-id/check?${query.toString()}`, fetchOptions)
  const result = await parseJson<UserIdCheckResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '아이디 확인에 실패했습니다.')
  }
  return result.data
}

export async function checkMemberLongtermCode(orgCode: string, excludeMCode?: string) {
  const query = new URLSearchParams({ orgCode })
  if (excludeMCode) {
    query.set('excludeMCode', excludeMCode)
  }
  const response = await apiFetch(`${BASE}/longterm-code/check?${query.toString()}`, fetchOptions)
  const result = await parseJson<LongtermCodeCheckResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '기관 기호 확인에 실패했습니다.')
  }
  return result.data
}

export async function fetchBizGubunList() {
  const response = await apiFetch(`${BASE}/biz-gubun`, fetchOptions)
  const result = await parseJson<BizGubunListResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '사업구분 목록을 불러오지 못했습니다.')
  }
  return result.data.items
}

export async function signUpMember(payload: SignUpPayload) {
  const response = await apiFetch(`${BASE}/signup`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<SignUpResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '회원가입에 실패했습니다.')
  }
  return result.data
}

export async function startMemberKakaoOAuth(payload: KakaoOAuthStartPayload) {
  const response = await apiFetch(`${BASE}/oauth/kakao/start`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<KakaoOAuthStartResult>(response)
  if (!result.success || !result.data?.url) {
    throw new Error(result.message || '카카오 로그인을 시작하지 못했습니다.')
  }
  return result.data
}

export async function completeMemberKakaoOAuth(payload: KakaoOAuthCompletePayload) {
  const response = await apiFetch(`${BASE}/oauth/kakao`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<KakaoOAuthCompleteResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '카카오 로그인에 실패했습니다.')
  }
  return result.data
}

export async function loginMember(payload: LoginPayload) {
  const response = await apiFetch(`${BASE}/login`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<LoginResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '로그인에 실패했습니다.')
  }
  return result.data
}

export async function withdrawMember(reason = '') {
  const response = await apiFetch(`${BASE}/withdraw`, {
    ...fetchOptions,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '탈퇴에 실패했습니다.')
  }
}

export async function logoutMember() {
  const response = await apiFetch(`${BASE}/logout`, {
    ...fetchOptions,
    method: 'POST',
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '로그아웃에 실패했습니다.')
  }
}

export async function fetchMemberSession() {
  const response = await apiFetch(`${BASE}/session`, fetchOptions)
  const result = await parseJson<SessionResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '로그인 세션을 확인할 수 없습니다.')
  }
  return result.data.member
}

export async function updateMemberContact(payload: { phone?: string; email?: string }) {
  const response = await apiFetch(`${BASE}/contact`, {
    ...fetchOptions,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<ProfileUpdateResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '연락처 저장에 실패했습니다.')
  }
  return result.data.member
}

export async function updateMemberProfile(payload: ProfileUpdatePayload) {
  const response = await apiFetch(`${BASE}/profile`, {
    ...fetchOptions,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const result = await parseJson<ProfileUpdateResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '내 정보 저장에 실패했습니다.')
  }
  return result.data.member
}

export async function saveMemberBizCert(url: string) {
  const response = await apiFetch(`${BASE}/biz-cert`, {
    ...fetchOptions,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  })
  const result = await parseJson<ProfileUpdateResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '사업자등록증 저장에 실패했습니다.')
  }
  return result.data.member
}

export async function uploadMemberBizCert(file: File) {
  const body = new FormData()
  body.append('file', file)
  const response = await apiFetch(`${BASE}/biz-cert`, {
    ...fetchOptions,
    method: 'POST',
    body,
  })
  const result = await parseJson<{ url: string; filename: string }>(response)
  if (!result.success || !result.data?.url) {
    throw new Error(result.message || '사업자등록증 업로드에 실패했습니다.')
  }
  return result.data
}
